import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AuthApiService } from '../../../core/auth/auth-api.service';
import { NotificationService } from '../../../services/notification.service';
import { LucideIconsModule } from '../../lucide-icons.module';
import { PhoneInputComponent } from '../phone-input/phone-input.component';
import { COUNTRIES } from '../../data/countries';
import * as AuthActions from '../../../store/auth/auth.actions';
import { ReauthDialogService } from './reauth-dialog.service';

type AuthMethod = 'password' | 'otp';
type IdentifierType = 'phone' | 'email';
type OtpStep = 'target' | 'code';

@Component({
    selector: 'app-reauth-dialog',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, LucideIconsModule, PhoneInputComponent],
    templateUrl: './reauth-dialog.component.html',
    styleUrls: ['./reauth-dialog.component.scss'],
})
export class ReauthDialogComponent {
    readonly dialog = inject(ReauthDialogService);
    private fb = inject(FormBuilder);
    private authApi = inject(AuthApiService);
    private store = inject(Store);
    private notifications = inject(NotificationService);

    method = signal<AuthMethod>('password');
    identifier = signal<IdentifierType>('email');
    otpStep = signal<OtpStep>('target');
    loading = signal(false);
    message = signal<string | null>(null);
    error = signal<string | null>(null);

    otpForm = this.fb.group({
        mobile: this.fb.control({ countryCode: 'EG', phone: '' }, Validators.required),
        email: this.fb.control('', [Validators.email]),
        code: this.fb.control<string>('', [])
    });

    passwordForm = this.fb.group({
        mobile: this.fb.control({ countryCode: 'EG', phone: '' }, Validators.required),
        email: this.fb.control('', [Validators.email]),
        password: this.fb.control('', [Validators.required])
    });

    get otpButtonLabel(): string {
        if (this.loading()) return this.otpStep() === 'target' ? 'Sending...' : 'Verifying...';
        return this.otpStep() === 'target' ? 'Send code' : 'Verify code';
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.dialog.isOpen()) this.dialog.close();
    }

    close(event?: Event): void {
        event?.stopPropagation();
        this.dialog.close();
        this.resetTransientState();
    }

    setMethod(method: AuthMethod) {
        if (this.method() === method) return;
        this.method.set(method);
        this.resetTransientState();
    }

    setIdentifier(identifier: IdentifierType) {
        if (this.identifier() === identifier) return;
        this.identifier.set(identifier);
        this.resetTransientState();
    }

    private resetTransientState(): void {
        this.message.set(null);
        this.error.set(null);
        this.loading.set(false);
        this.otpStep.set('target');
        this.otpForm.patchValue({ code: '' });
    }

    async onOtpSubmit(): Promise<void> {
        this.message.set(null);
        this.error.set(null);

        const target =
            this.identifier() === 'phone'
                ? this.toE164(this.otpForm.value.mobile)
                : String(this.otpForm.value.email || '').trim();

        if (!target) {
            this.error.set(this.identifier() === 'phone' ? 'Enter a valid phone number.' : 'Enter a valid email.');
            return;
        }

        if (this.otpStep() === 'target') {
            this.loading.set(true);
            if (this.identifier() === 'phone') {
                this.authApi.sendOtp(target).subscribe({
                    next: () => {
                        this.loading.set(false);
                        this.otpStep.set('code');
                        this.message.set(`Code sent to ${target}`);
                    },
                    error: (err: any) => {
                        this.loading.set(false);
                        this.error.set(this.extractErrorMessage(err, 'Failed to send code'));
                    }
                });
            } else {
                this.authApi.sendEmailOtp(target).subscribe({
                    next: () => {
                        this.loading.set(false);
                        this.otpStep.set('code');
                        this.message.set(`Code sent to ${target}`);
                    },
                    error: (err: any) => {
                        this.loading.set(false);
                        this.error.set(this.extractErrorMessage(err, 'Failed to send code'));
                    }
                });
            }
            return;
        }

        const code = String(this.otpForm.value.code || '').trim();
        if (!code) {
            this.error.set('Enter the code.');
            return;
        }

        this.loading.set(true);
        const verify$ = this.identifier() === 'phone' ? this.authApi.verifyOtp(target, code) : this.authApi.verifyEmailOtp(target, code);
        verify$.subscribe({
            next: async (session) => {
                this.loading.set(false);
                await this.onSession(session.access_token);
            },
            error: (err) => {
                this.loading.set(false);
                this.error.set(this.extractErrorMessage(err, 'Invalid code'));
            }
        });
    }

    resendOtp(): void {
        const target =
            this.identifier() === 'phone'
                ? this.toE164(this.otpForm.value.mobile)
                : String(this.otpForm.value.email || '').trim();

        if (!target) {
            this.error.set(this.identifier() === 'phone' ? 'Enter a valid phone number.' : 'Enter a valid email.');
            return;
        }

        this.loading.set(true);
        if (this.identifier() === 'phone') {
            this.authApi.sendOtp(target).subscribe({
                next: () => {
                    this.loading.set(false);
                    this.message.set(`Code resent to ${target}`);
                },
                error: (err: any) => {
                    this.loading.set(false);
                    this.error.set(this.extractErrorMessage(err, 'Failed to resend code'));
                }
            });
        } else {
            this.authApi.sendEmailOtp(target).subscribe({
                next: () => {
                    this.loading.set(false);
                    this.message.set(`Code resent to ${target}`);
                },
                error: (err: any) => {
                    this.loading.set(false);
                    this.error.set(this.extractErrorMessage(err, 'Failed to resend code'));
                }
            });
        }
    }

    onPasswordSubmit(): void {
        this.message.set(null);
        this.error.set(null);

        const password = String(this.passwordForm.value.password || '');
        if (!password) {
            this.error.set('Enter your password.');
            return;
        }

        if (this.identifier() === 'phone') {
            const phoneE164 = this.toE164(this.passwordForm.value.mobile);
            if (!phoneE164) {
                this.error.set('Enter a valid phone number.');
                return;
            }

            this.loading.set(true);
            this.authApi.signInWithPhone(phoneE164, password).subscribe({
                next: async (session) => {
                    this.loading.set(false);
                    await this.onSession(session.access_token);
                },
                error: (err) => {
                    this.loading.set(false);
                    this.error.set(this.extractErrorMessage(err, 'Sign in failed'));
                }
            });
            return;
        }

        const email = String(this.passwordForm.value.email || '').trim();
        if (!email) {
            this.error.set('Enter a valid email.');
            return;
        }

        this.loading.set(true);
        this.authApi.signInWithEmail(email, password).subscribe({
            next: async (session) => {
                this.loading.set(false);
                await this.onSession(session.access_token);
            },
            error: (err) => {
                this.loading.set(false);
                this.error.set(this.extractErrorMessage(err, 'Sign in failed'));
            }
        });
    }

    private async onSession(accessToken: string): Promise<void> {
        this.store.dispatch(AuthActions.setAccessToken({ accessToken }));
        this.store.dispatch(AuthActions.checkAuthSuccess({ isAuthenticated: true }));
        this.store.dispatch(AuthActions.loadCurrentUser());
        this.notifications.showSuccess('Signed in successfully.').subscribe();
        this.dialog.close();
        this.resetTransientState();
    }

    private toE164(value: any): string | null {
        const countryCode = String(value?.countryCode || '').toUpperCase().trim();
        const rawPhone = String(value?.phone || '');
        const digits = rawPhone.replace(/[^\d]/g, '').replace(/^0+/, '');

        if (!countryCode || !digits) return null;
        const match = COUNTRIES.find((c) => c.code === countryCode);
        const dial = match?.dial_code || '';
        if (!dial || !dial.startsWith('+')) return null;

        return `${dial}${digits}`;
    }

    private extractErrorMessage(err: any, fallback: string): string {
        return err?.error?.error?.message || err?.error?.message || err?.message || fallback;
    }
}
